import Vue from "vue";
import Router from "vue-router";
import { isLoggedIn, isValidLoginToken } from "./services/UserService";

Vue.use(Router);

async function guard(to, from, next) {
  const isValidToken = await isValidLoginToken();
  if (!isValidToken) {
    // Redirect to sign-in page if token not valid on server is invalid.
    next("/signin");
  } else {
    if (isLoggedIn()) {
      next();
    } else {
      // Redirect to sign-in page if client auth object is invalid.
      next("/signin");
    }
  }
}

export default new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      name: "root",
      component: () => import("./views/Dashboard.vue"),
      children: [
        {
          path: "",
          name: "home",
          beforeEnter: (to, from, next) => {
            if (isLoggedIn()) {
              next("/dashboard");
            } else {
              next();
            }
          },
          component: () => import("./views/Home.vue"),
        },
        {
          path: "dashboard",
          name: "dashboard",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/Index.vue"),
        },
        {
          path: "contents",
          name: "contents",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/ContentsList.vue"),
        },
        {
          path: "experiences",
          name: "experiences",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/Experiences.vue"),
        },
        {
          path: "experiences/form",
          name: "create-experience",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/ExperienceForm.vue"),
        },
        {
          path: "experiences/form/:id",
          name: "edit-experience",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/ExperienceForm.vue"),
        },
        {
          path: "content",
          name: "content",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/Content.vue"),
        },
        {
          path: "promotions",
          name: "promotions",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/Promotions.vue"),
        },
        {
          path: "promotions/form",
          name: "create-promotions",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/PromotionsForm.vue"),
        },
        {
          path: "offers",
          name: "offers",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/OfferList.vue"),
        },
        {
          path: "contests",
          name: "contests",
          beforeEnter: guard,
          component: () => import("./views/Contests/ContestsList.vue"),
        },
        {
          path: "tour-page",
          name: "tour page",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/Tour.vue"),
        },
        {
          path: "event-page",
          name: "event page",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/Events.vue"),
        },

        {
          path: "vouchers",
          name: "vouchers",
          beforeEnter: guard,
          component: () => import("./views/Dashboard/VouchersList.vue"),
        },
        {
          path: "rewarder",
          name: "rewarder",
          beforeEnter: guard,
          component: () => import("./views/Rewarder/MyRewards.vue"),
        },
        {
          path: "trainer",
          name: "trainer",
          beforeEnter: guard,
          component: () => import("./views/Trainer/Trainer.vue"),
        },
        {
          path: "community",
          name: "community",
          beforeEnter: guard,
          component: () => import("./views/Community/Community.vue"),
        },
      ],
    },
    {
      path: "/content",
      beforeEnter: guard,
      component: () => import("./views/Dashboard.vue"),
      children: [
        {
          path: "stories",
          name: "stories",
          component: () => import("./views/Content/StoriesContent.vue"),
        },
      ],
    },
    {
      path: "/inspirator",
      name: "inspirator",
      component: () => import("./views/Inspirator/Index.vue"),
      children: [
        {
          path: "",
          name: "inspirator-index",
          component: () => import("./views/Inspirator/List.vue"),
        },
      ],
    },
    {
      path: "/promoter",
      beforeEnter: guard,
      component: () => import("./views/Promoter/Index.vue"),
      children: [
        {
          path: "",
          name: "promotions",
          component: () => import("./views/Promoter/MyPromotions.vue"),
        },
        {
          path: "boost",
          name: "boost",
          component: () => import("./views/Promoter/Boost.vue"),
        },
        {
          path: "advertise",
          name: "advertise",
          component: () => import("./views/Promoter/Advertise.vue"),
        },{
          path: "message",
          name: "message",
          component: () => import("./views/Promoter/Message.vue"),
        },
      ],
    },
    {
      path: "/account",
      name: "account",
      beforeEnter: guard,
      component: () => import("./views/Account.vue"),
      children: [
        { path: "", component: () => import("./views/Account/Account.vue") },
        {
          path: "activate-account",
          component: () => import("./views/Account/Activate.vue"),
        },
        {
          path: "personal-detail",
          component: () => import("./views/Account/PersonalDetail.vue"),
        },
        {
          path: "business-detail",
          component: () => import("./views/Account/BusinessDetail.vue"),
        },
        { path: "Users", component: () => import("./views/Account/Users.vue") },
        {
          path: "security",
          component: () => import("./views/Account/Security.vue"),
        },
        {
          path: "contact-us",
          component: () => import("./views/Account/ContactUs.vue"),
        },
        {
          path: "user-credit",
          component: () => import("./views/Account/UserCredit.vue"),
        },
        {
          path: "account-status",
          component: () => import("./views/Account/AccountStatus.vue"),
        },
        {
          path: "messaging-setting",
          component: () => import("./views/Account/MessagingSetting.vue"),
        },
        {
          path: "introductions",
          component: () => import("./views/Account/Introductions.vue"),
        },
      ],
    },
    {
      path: "/account/reset-password",
      name: "reset-password",
      component: () => import("./views/Account/ResetPassword.vue"),
    },
    {
      path: "/account/activate",
      name: "account-activate",
      component: () => import("./views/Account/Activate.vue"),
    },
    {
      path: "/test",
      name: "test-component",
      component: () => import("./views/TestComponent.vue"),
    },
    {
      path: "/signin",
      name: "signin",
      component: () => import("./views/Signin.vue"),
    },
    {
      path: "/signup",
      name: "signup",
      component: () => import("./views/Signup/Index.vue"),
    },
    {
      path: "/lists",
      beforeEnter: guard,
      component: () => import("./views/Dashboard.vue"),
      children: [
        {
          path: "my-lists",
          name: "mylists",
          component: () => import("./views/Lists/MyLists.vue"),
        },
        {
          path: "experiences-lists",
          name: "experiences-lists",
          component: () => import("./views/Lists/ExperiencesLists.vue"),
        },
        {
          path: "tours-lists",
          name: "tours-lists",
          component: () => import("./views/Lists/ToursLists.vue"),
        },
        {
          path: "events-lists",
          name: "events-lists",
          component: () => import("./views/Lists/EventsLists.vue"),
        },
        {
          path: "stories-lists",
          name: "stories-lists",
          component: () => import("./views/Lists/StoriesLists.vue"),
        },
      ],
    },
    {
      path: "/contests",
      beforeEnter: guard,
      component: () => import("./views/Dashboard.vue"),
      children: [
        {
          path: "join",
          name: "join-contest",
          component: () => import("./views/Contests/Join.vue"),
        },
        {
          path: "results",
          name: "contest-results",
          component: () => import("./views/Contests/Results.vue"),
        },
      ],
    },
    {
      path: "/analytics",
      beforeEnter: guard,
      component: () => import("./views/Dashboard.vue"),
      children: [
        {
          path: "",
          name: "analytics",
          component: () => import("./views/Analytics/Index.vue"),
        },
      ],
    },
    {
      path: "/rewarder",
      beforeEnter: guard,
      component: () => import("./views/Dashboard.vue"),
      children: [
        {
          path: "badges",
          name: "badges",
          component: () => import("./views/Rewarder/MyBadges.vue"),
        },
      ],
    },
  ],
});
